import React, { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Loader2, X, Tag, Check } from "lucide-react";
import {
  adminGetCategories, adminCreateCategory, adminUpdateCategory, adminDeleteCategory,
} from "../../api";

const emptyCat = () => ({ name: { pl: "", en: "", uk: "", es: "" } });

const CategoryEditor = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial || emptyCat());
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);

  const setName = (lang, val) =>
    setForm((f) => ({ ...f, name: { ...f.name, [lang]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name: form.name };
      const saved = isEdit
        ? await adminUpdateCategory(initial.id, payload)
        : await adminCreateCategory(payload);
      onSaved(saved, isEdit);
    } catch (e) {
      alert("Nie udało się zapisać kategorii.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm p-4 py-16">
      <div className="glass-card w-full max-w-lg rounded-3xl p-7 sm:p-8">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold uppercase tracking-wide text-white">
            {isEdit ? "Edytuj kategorię" : "Nowa kategoria"}
          </h3>
          <button onClick={onClose} className="glass-btn p-2">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-6">
          <label className="mb-2 block text-sm font-medium text-white/75">Nazwa kategorii (PL / EN / UA / ES)</label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {["pl", "en", "uk", "es"].map((l) => (
              <input
                key={l}
                value={form.name[l] || ""}
                onChange={(e) => setName(l, e.target.value)}
                placeholder={l.toUpperCase()}
                className="w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none focus:border-white/45"
              />
            ))}
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button onClick={onClose} className="glass-btn px-5 py-2.5 text-sm font-medium">Anuluj</button>
          <button
            onClick={save}
            disabled={saving || !form.name.pl}
            className="glass-btn-solid glass-btn px-6 py-2.5 text-sm font-semibold disabled:opacity-50"
          >
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Zapisywanie...</> : <><Check className="h-4 w-4" /> Zapisz</>}
          </button>
        </div>
      </div>
    </div>
  );
};

const CategoriesPanel = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => {
    setLoading(true);
    adminGetCategories()
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const onSaved = (saved, isEdit) => {
    setCategories((cs) =>
      isEdit ? cs.map((c) => (c.id === saved.id ? saved : c)) : [...cs, saved]
    );
    setOpen(false);
    setEditing(null);
  };

  const remove = async (id) => {
    if (!window.confirm("Usunąć tę kategorię?")) return;
    try {
      await adminDeleteCategory(id);
      setCategories((cs) => cs.filter((c) => c.id !== id));
    } catch (e) {
      const msg = e?.response?.data?.detail || "Nie udało się usunąć kategorii.";
      alert(msg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-white/50">
        <Loader2 className="h-5 w-5 animate-spin" /> Ładowanie kategorii...
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-white">Kategorie ofert</h2>
        <button
          onClick={() => { setEditing(null); setOpen(true); }}
          className="glass-btn-solid glass-btn px-5 py-2.5 text-sm font-semibold"
        >
          <Plus className="h-4 w-4" /> Dodaj kategorię
        </button>
      </div>

      <p className="mb-6 text-sm text-white/50">
        Kategorie służą jako zakładki/filtry na stronie ofert pracy. Edytuj nazwy w 3 językach lub dodaj nowe.
      </p>

      {categories.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/5 py-20 text-white/50">
          <Tag className="h-8 w-8" />
          <p className="text-sm">Brak kategorii. Dodaj pierwszą!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {categories.map((c) => (
            <div
              key={c.id}
              className="glass-card flex items-center justify-between gap-4 rounded-2xl p-5"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 border border-white/20">
                  <Tag className="h-4 w-4 text-white" />
                </span>
                <div>
                  <h4 className="font-display text-lg font-semibold text-white">{c.name?.pl}</h4>
                  <p className="text-xs text-white/50">{c.name?.en} · {c.name?.uk}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { setEditing(c); setOpen(true); }}
                  className="glass-btn px-4 py-2 text-xs font-medium"
                >
                  <Pencil className="h-4 w-4" /> Edytuj
                </button>
                <button
                  onClick={() => remove(c.id)}
                  className="glass-btn px-4 py-2 text-xs font-medium text-red-300 hover:text-red-200"
                >
                  <Trash2 className="h-4 w-4" /> Usuń
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <CategoryEditor
          initial={editing}
          onClose={() => { setOpen(false); setEditing(null); }}
          onSaved={onSaved}
        />
      )}
    </div>
  );
};

export default CategoriesPanel;
